import type { RoomOptions } from "livekit-client";
import { enterCallAudioSession } from "./callAudioSession";

export interface CallRoomDevices {
  /** Context created in the Join gesture; absent after a reload-resume. */
  audioContext?: AudioContext;
  micDeviceId?: string;
  camDeviceId?: string;
  speakerDeviceId?: string;
}

/**
 * The LiveKit options every call joins with.
 *
 * One function so the audio regression suite (`tests/call-audio/`) runs the
 * exact configuration real calls use -- a test with its own copy would keep
 * passing while production drifted.
 *
 * A bare deviceId is an `ideal` constraint: a device that has gone missing
 * falls back to the OS default instead of failing the join.
 *
 * Adaptive Stream subscribes each remote video at the size it is shown and
 * pauses it while nobody shows it; Dynacast stops sending simulcast layers
 * nobody subscribes to. Both cut bandwidth on weak mobile links and leave
 * audio untouched (#2223). Adaptive Stream only counts videos attached with
 * the SDK's `attach()` -- anything playing a remote track some other way
 * (the phone picture-in-picture video does) must attach it too, or that
 * video freezes. Background pausing is off: on a phone the floating video is
 * exactly what plays while the page is hidden.
 */
export function buildCallRoomOptions({
  audioContext,
  micDeviceId,
  camDeviceId,
  speakerDeviceId,
}: CallRoomDevices): RoomOptions {
  return {
    webAudioMix: audioContext ? { audioContext } : true,
    adaptiveStream: { pauseVideoInBackground: false },
    dynacast: true,
    ...(micDeviceId ? { audioCaptureDefaults: { deviceId: micDeviceId } } : {}),
    ...(camDeviceId ? { videoCaptureDefaults: { deviceId: camDeviceId } } : {}),
    ...(speakerDeviceId ? { audioOutput: { deviceId: speakerDeviceId } } : {}),
  };
}

/**
 * The AudioContext a call mixes into. Call it synchronously inside the Join
 * click, before any await: Safari (and iOS) start a new context suspended and
 * only let it resume during a user gesture, so it is resumed right here too.
 * Left suspended, the call joins without sound and nothing later can resume
 * it except another gesture.
 */
export function createJoinAudioContext(): AudioContext | undefined {
  // Before the context starts, so iOS plays it as a call (silent switch).
  enterCallAudioSession();
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return undefined;
  const context = new Ctor();
  void context.resume().catch(() => {});
  return context;
}
